import * as IOpolyfill from 'intersection-observer';
import 'url-search-params-polyfill';
import 'whatwg-fetch';
import { ResizeObserver } from '@juggle/resize-observer';
import AdPlacement from './AdPlacement';
/* eslint-disable-next-line */
import ConsoleLogger from './loggers/ConsoleLogger';
import failSafeTemplate from '../templates/failsafe';

import {
  buildSibDabuUrl,
  getUnmountedPlacements,
  getSibLinks,
  mountSibsToTargetNodes,
  mountSibResponse,
} from './ads/sibAds';
import {
  getBingAds,
  mountBingResponse,
  generateTelemetryScripts,
  mountTelemetryScript,
} from './ads/bingAds';
import { getBingJSAds, mountBingJsScript } from './ads/bingJSAds';
import { getGoogleAFSParams, mountGoogleAFSAds } from './ads/afsAds';
import { getGoogleRSParams, mountGoogleRS } from './ads/grsAds';
import { getGoogleRSParamsV2, mountGoogleRSV2 } from './ads/grsAdsV2';
import mountJustWatchWidget from './ads/justWatchWidget';
import { mountMgmAds, mountMgmAdByPlacementId } from './ads/mgmAds';
import {
  initPixelHandler,
  firePageViewPixel,
  getGoogleAdsClickTrackUrl,
  getBingJsAdsClickTrackUrl,
  getAfsConversionPixelUrls,
  fireConversionPixel,
  watchForCsaCookie,
  getDocumentCookies,
} from './pixel';

class AmgAds {
  constructor(doc, win) {
    this.doc = doc;
    this.win = win;
    if (!this.win.ResizeObserver) this.win.ResizeObserver = ResizeObserver;
    this.script = this.getCurrentScript();
    this.debugMode = false;
    this.logger = new ConsoleLogger(this);
    this.dataset = this.getValuesFromDataset();
    this.sourceUrl = this.dataset.dabuSourceUrl || win.location.toString();
    this.dabuUrl = AmgAds.getDabuBaseUrl(this.dataset.dabuEnvFromScript);
    this.pageParams = this.getParamsFromPage();
    this.version = this.getVersion();
    this.env = process.env.ENV || this.dataset.dabuEnvFromScript;
    this.adContext = {
      abTestIds: this.win.ss_watts,
      advertisers: [],
    };
    this.campaignID = this.resolveCampaignID();
    this.adPlacements = {};
    this.partner = '';
    this.pvId = AmgAds.generateUUID();
    this.ueId = this.dataset.ueid || AmgAds.generateUUID(); // unique entry ID
    this.monetizationParamsList = ['ad', 'ag', 'an', 'o', 'rch', 'crch', 'rtb', 'rty'];
    this.isFailOver = false;
    this.agenciesMap = {
      // TODO: move agency-specific pixel logic in this mapping
      amg: { typeTag: '10000' },
      fw10: { partner: 'Elevated Media', typeTag: '10001' },
      fw46: { partner: 'Knowledge Campus', typeTag: '10002' },
      fw55: { partner: 'Titanium', typeTag: '10003' },
      fw63: { partner: 'IBT', typeTag: '10004' },
      fw64: { partner: 'Entercom', typeTag: '10005' },
      fw65: { partner: 'Andrews McMeel Universal', typeTag: '10006' },
      fw66: { partner: 'IBTTimes', typeTag: '10007' },
      fw67: { partner: 'Investorplace', typeTag: '10008' },
      fw68: { partner: 'postmediasolutions', typeTag: '10009' },
      fw69: { partner: 'smartrtb', typeTag: '10010' },
      fw70: { partner: 'smartadserver', typeTag: '10011' },
      fw71: { partner: 'GumGum', typeTag: '10012' },
      fw72: { partner: 'LiquidM', typeTag: '10013' },
      fw73: { partner: 'Motley Fool', typeTag: '10014' },
      fw78: { partner: 'Leaf Group', typeTag: '10015' },
      fw79: { partner: 'Sonobi', typeTag: '10016' },
      fw29: { partner: 'Red GoBo', typeTag: '10017' },
      fw53: { partner: 'West Bay Tech', typeTag: '10018' },
    };
    this.isCurrentlyPolling = false;
    this.pollingFns = [];
    this.pixelHandler = null;
  }

  // PARAM METHODS
  static getDabuBaseUrl(dabuEnvFromScript) {
    const dabuUrlsByEnv = {
      dev: 'http://dabu.development.us-east-1.aws.ask.info',
      staging: 'http://dabu.staging.us-east-1.aws.ask.info',
      test: 'http://localhost:8081',
      use1: 'https://dabu.askmediagroup.com',
    };
    const env = process.env.ENV;
    return dabuUrlsByEnv[env] || dabuUrlsByEnv[dabuEnvFromScript] || dabuUrlsByEnv.use1;
  }

  getCurrentScript() {
    if (this.doc.currentScript) return this.doc.currentScript;
    // the below code handles ie not supporting currentScript
    // it was borrowed from https://github.com/amiller-gh/currentScript-polyfill/blob/master/currentScript.js
    try {
      throw new Error();
    } catch (err) {
      // Find the second match for the "at" string to get file src url from stack.
      // Specifically works with the format of stack traces in IE.
      let i = 0;
      const stackDetails = /.*at [^(]*\((.*):(.+):(.+)\)$/gi.exec(err.stack);
      const scriptLocation = (stackDetails && stackDetails[1]) || false;
      const line = (stackDetails && stackDetails[2]) || false;
      const currentLocation = document.location.href.replace(document.location.hash, '');
      let pageSource;
      let inlineScriptSourceRegExp;
      let inlineScriptSource;
      const scripts = document.getElementsByTagName('script'); // Live NodeList collection

      if (scriptLocation === currentLocation) {
        pageSource = document.documentElement.outerHTML;
        inlineScriptSourceRegExp = new RegExp(
          `(?:[^\\n]+?\\n){0,${line - 2}}[^<]*<script>([\\d\\D]*?)<\\/script>[\\d\\D]*`,
          'i',
        );
        inlineScriptSource = pageSource.replace(inlineScriptSourceRegExp, '$1').trim();
      }

      for (; i < scripts.length; i++) {
        // If ready state is interactive, return the script tag
        if (scripts[i].className.indexOf('inited') === -1) {
          if (scripts[i].readyState === 'interactive') {
            scripts[i].className += ' inited';
            return scripts[i];
          }

          // If src matches, return the script tag
          if (scripts[i].src === scriptLocation) {
            scripts[i].className += ' inited';
            return scripts[i];
          }

          // If inline source matches, return the script tag
          if (
            scriptLocation === currentLocation
            && scripts[i].innerHTML
            && scripts[i].innerHTML.trim() === inlineScriptSource
          ) {
            scripts[i].className += ' inited';
            return scripts[i];
          }
        }
      }
      // If no match, return null
      return null;
    }
  }

  getValuesFromDataset() {
    const { dataset = {} } = this.script;
    return {
      adOpen: dataset.adOpen || 'tab',
      adType: dataset.adType || 'sib',
      dabuAdSlot: dataset.dabuSl || null,
      dabuCategory: dataset.dabuCategory || null,
      dabuCampaignId: dataset.dabuCampaignId || null,
      dabuClickUrl: dataset.dabuC || null,
      dabuCreativeId: dataset.dabuCreativeId || null,
      dabuEnvFromScript: dataset.dabuEnvFromScript,
      dabuHeight: dataset.dabuHeight || null,
      dabuLp: dataset.dabuLp || null,
      dabuPid: dataset.dabuPid || 'amg',
      dabuPixelId: dataset.dabuPixelId || null,
      dabuPlacementId: dataset.dabuPlacementId || '',
      dabuSourceUrl: dataset.dabuSu || null,
      dabuWidth: dataset.dabuWidth || null,
      defaultOcode: dataset.defaultOcode || null,
      generator: dataset.generator || null,
      gStyleId: dataset.gStyleId || null,
      kwSource: dataset.kwSource || null,
      sibTemplate: dataset.sibTemplate || null,
      signals: dataset.signals || null,
      timeout: dataset.timeout || 0,
      ueid: dataset.ueid || null,
      mgmZoneId: dataset.mgmZoneId || null,
    };
  }

  getVersion() {
    const { src } = this.script;
    const versionRegex = src.match(/(v\d*\.\d*\.\d*)/);
    return versionRegex ? versionRegex[0] : 'latest';
  }

  getParamsFromPage() {
    const { sourceUrl } = this;
    if (!sourceUrl || !this.isMacroResolved(sourceUrl)) {
      return {};
    }

    const { searchParams } = new URL(sourceUrl);
    const utmContent = searchParams.get('utm_content');

    const cookiesObj = this.getDocumentCookies();
    let monetizeParamObj = {};

    if (utmContent && utmContent.includes('params:')) {
      // 7 because of the length of string 'params:'
      const utmParams = utmContent.substring(7);
      monetizeParamObj = Object.fromEntries(new URLSearchParams(utmParams));
    } else if (cookiesObj.utm_content && cookiesObj.utm_content.includes('params:')) {
      const utmCookieParams = cookiesObj.utm_content.substring(7);
      monetizeParamObj = Object.fromEntries(new URLSearchParams(utmCookieParams));
    } else {
      if (cookiesObj.iac_an) {
        monetizeParamObj.an = cookiesObj.iac_an;
      }
      if (cookiesObj.iac_ad) {
        monetizeParamObj.ad = cookiesObj.iac_ad;
      }
      if (cookiesObj.iac_ag) {
        monetizeParamObj.ag = cookiesObj.iac_ag;
      }
      if (cookiesObj.iac_rch) {
        monetizeParamObj.rch = cookiesObj.iac_rch;
      }
      if (cookiesObj.iac_crch) {
        monetizeParamObj.crch = cookiesObj.iac_crch;
      }
      if (cookiesObj.iac_o) {
        monetizeParamObj.o = cookiesObj.iac_o;
      }
      if (cookiesObj.rtb) {
        monetizeParamObj.rtb = cookiesObj.rtb;
      }
    }
    // direct params have the highest priority,
    // we will overwrite any previously set monetizeParamObj here
    const urlParamsObj = Object.fromEntries(searchParams);
    Object.assign(monetizeParamObj, urlParamsObj);

    // assign default ocode for organic
    if (!monetizeParamObj.o && this.dataset.defaultOcode) {
      monetizeParamObj.o = this.dataset.defaultOcode;
    }
    // Init Logger
    this.debugMode = monetizeParamObj.fmt === 'debug' || monetizeParamObj.fmt === 'debugstart';
    this.logger.debugMode = this.debugMode;
    this.logger.debug('Getting Page Params', monetizeParamObj);

    return monetizeParamObj;
  }

  resolveCampaignID() {
    const { dabuCampaignId, dabuCreativeId } = this.dataset;
    // campaignID=mockCID& OR campaignID%3DmockCID%26
    const regExpCampaignID = /campaignID(?:=|%3D)(\w+)(?:&|%26)?/;
    const campaignIDMatch = regExpCampaignID.exec(this.win.location.search);
    const campaignIDFromUrl = campaignIDMatch && campaignIDMatch[1];
    let campaignID = dabuCampaignId; // default
    if (dabuCreativeId && !dabuCampaignId) {
      // legacy pattern
      const partnerId = this.win.location.host;
      campaignID = `${partnerId}_${dabuCreativeId}`;
    } else if (campaignIDFromUrl) {
      // campaign_id in the url
      campaignID = campaignIDFromUrl;
    }
    return campaignID;
  }

  // Ad Placement methods
  initAdPlacements() {
    const { dabuPlacementId = '', adType } = this.dataset;
    let idsQueue = dabuPlacementId.split(',');
    if (adType === 'bing') {
      const { bingOptions } = this.win.amgAds || {};

      if (!bingOptions && !dabuPlacementId) {
        throw new Error('No Bing Options or dabu-placement-id');
      }
      if (bingOptions && bingOptions.blocks) {
        // only override placementId if bingOptions.blocks is truthy,
        // this will allow us to support legacy implemntations
        idsQueue = bingOptions.blocks.map((b) => b.placementId);
      }
    } else if (adType === 'bingJS') {
      const { bingJsOptions } = this.win.amgAds || {};

      if (!bingJsOptions && !dabuPlacementId) {
        throw new Error('No Bing JS Options or dabu-placement-id');
      }
      if (bingJsOptions && bingJsOptions.blocks) {
        // only override placementId if bingJsOptions.blocks is truthy,
        // this will allow us to support legacy implemntations
        idsQueue = bingJsOptions.blocks.map((b) => b.placementId);
      }
    } else if (!dabuPlacementId) {
      throw new Error('No dabu-placement-id');
    }
    const { kwCount } = this.pageParams;
    idsQueue.forEach((placementId, idx) => {
      const placement = new AdPlacement(placementId, idx, kwCount);
      placement.initLogger(adType, this);
      this.adPlacements[placementId] = placement;
    });
  }

  initializeAds() {
    const { adType } = this.dataset;

    const initFuncsByAdType = {
      sib: this.mountSibsToTargetNodes,
      bing: this.getBingAds,
      bingJS: this.getBingJSAds,
      afs: this.getGoogleAFSParams,
      grs: this.getGoogleRSParams,
      grs2: this.getGoogleRSParamsV2,
      jw: this.mountJustWatchWidget,
      mgm: this.mountMgmAds,
      default: () => {
        throw new Error('adType is not supported.');
      },
    };

    this.initAdPlacements();

    const initFunc = initFuncsByAdType[adType] || initFuncsByAdType.default;

    initFunc.call(this);
  }

  // DABU CALLING METHODS
  callDabu(url) {
    return new Promise((resolve, reject) => {
      fetch(url)
        .then((res) => res.json())
        .then((res) => {
          if (res.ocode) this.initPixelHandler(res.ocode);
          resolve(res);
        })
        .catch((err) => {
          this.logger.error('Error calling dabu', err);
          reject(err);
        });
    });
  }

  mountDabuResponse(response, placementId) {
    const template = document.createElement('div');
    template.innerHTML = response.content.trim();

    const mountingNode = document.getElementById(placementId);
    const cb = this.win?.amgAds?.adsLoadedCallback || function () {};

    if (mountingNode) {
      mountingNode.appendChild(template);

      const style = document.createElement('style');
      style.innerHTML = response.stylesInnerText;
      document.querySelector('head').appendChild(style);

      const links = this.getClickableLinks(placementId);

      this.attachClickHandlers(links, placementId);
      this.watchForView(placementId);

      const { logger } = this.adPlacements[placementId];

      // Only logging ContentRendered event once if there are multiple placement
      // Log after the last placement is mounted
      const adPlacementsArr = Object.keys(this.adPlacements);
      if (adPlacementsArr.length === 1 || adPlacementsArr[1] === placementId) {
        const payload = logger.getContentRenderedPayload({
          eventType: 'ContentRendered',
          numLinks: links.length,
          adResultsDisplayedTop: response.adResultsDisplayedTop,
          adResultsDisplayedBottom: response.adResultsDisplayedBottom,
          adResultsDisplayed: response.adResultsDisplayed,
          ...response,
        });
        logger.logEvent(payload);
      }

      if (this.isFailOver) {
        logger.debug('Rendering Failsafe.');
      } else {
        logger.debug('Mounting Dabu Res to dom');
      }
      cb(null, placementId);
    } else {
      const err = new Error('Unable to mount node: placementId not found.');
      cb(err, placementId);
      throw err;
    }
  }

  // VIEWABILITY METHODS
  watchForView(placementId) {
    const sibElement = document.getElementById(placementId);

    const IntersectionObserver = window.IntersectionObserver || IOpolyfill;

    const sibInViewObserver = new IntersectionObserver((entries) => {
      if (entries[0].intersectionRatio > 0) {
        // we can safely reference `this` in here ONLY because we're using an arrow function
        const { logger } = this.adPlacements[placementId];
        const gaPayload = logger.getInViewPayload(this.isFirstAdBlock(placementId));
        logger.logEvent(gaPayload);
        sibInViewObserver.unobserve(sibElement);
      }
    });

    sibInViewObserver.observe(sibElement);
  }

  // SIB LANDING PAGE URL METHODS
  stripOldMonetizationParams(url) {
    if (!url) {
      return '';
    }
    const urlObj = new URL(url);
    this.monetizationParamsList.forEach((param) => {
      urlObj.searchParams.delete(param);
    });

    return urlObj.toString();
  }

  /**
   * Append any necessary param from source URL to the landing page URL
   * @param {String} lpUrl
   */
  appendSURLParamsToLpURL(lpUrl) {
    let lpUrlObj;
    try {
      lpUrlObj = new URL(lpUrl);
    } catch {
      console.error('not a valid LP URL.');
      return lpUrl;
    }

    const {
      cid,
      gclid = '',
      fbclid = '',
      clickID = '',
      taboola_click_id: taboolaClickId,
      site_id: taboolaSiteId,
      sclid,
      rc_click_id: rcClickId,
      uCampaign,
    } = this.pageParams;

    // Click Params
    if (gclid) {
      lpUrlObj.searchParams.set('gclid', gclid);
    }
    if (cid) {
      lpUrlObj.searchParams.set('cid', cid);
    }
    if (fbclid) {
      lpUrlObj.searchParams.set('fbclid', fbclid);
    }
    if (clickID) {
      lpUrlObj.searchParams.set('clickID', clickID);
    }
    if (taboolaClickId) {
      lpUrlObj.searchParams.set('taboola_click_id', taboolaClickId);
    }
    if (sclid) {
      lpUrlObj.searchParams.set('sclid', sclid);
    }
    if (rcClickId) {
      lpUrlObj.searchParams.set('rc_click_id', rcClickId);
    }

    // Other param
    if (taboolaSiteId) {
      lpUrlObj.searchParams.set('site_id', taboolaSiteId);
    }
    if (uCampaign) {
      lpUrlObj.searchParams.set('utm_campaign', uCampaign);
    }

    return lpUrlObj.toString();
  }

  buildSibLpUrl(redirectUrl, sibLpUrl) {
    const lpUrlDecorators = {
      'ob media': (lpUrl) => `${lpUrl}&t2=${this.pvId}&t3=${encodeURIComponent(this.sourceUrl || '')}`,
      peak226: (lpUrl) => {
        const { click_id: peakImpId } = this.pageParams;
        const impId = peakImpId ? `&peak_imp_id=${encodeURIComponent(peakImpId)}` : '';
        return `${lpUrl}&pvid=${this.pvId}${impId}`;
      },
      default: (lpUrl) => {
        let finalURL = `${lpUrl}&pvid=${this.pvId}&ueid=${this.ueId}`;

        if (AmgAds.hasAllMonetizationParams(this.pageParams)) {
          finalURL = this.stripOldMonetizationParams(finalURL);
          finalURL = finalURL.replace('/ar', '/web');
          const monetizationParams = Object.keys(this.pageParams).map((key) => {
            if (this.monetizationParamsList.includes(key)) {
              return `&${key}=${this.pageParams[key]}`;
            }
            return '';
          });
          finalURL += monetizationParams.join('');
        }

        return this.appendSURLParamsToLpURL(finalURL);
      },
    };

    const lowerPartner = this.partner;
    const decoratorFn = lpUrlDecorators[lowerPartner] || lpUrlDecorators.default;
    const finalUrl = decoratorFn(sibLpUrl);
    if (redirectUrl && this.isMacroResolved(redirectUrl)) {
      return redirectUrl + window.encodeURIComponent(finalUrl);
    }

    return finalUrl;
  }

  // CLICK METHODS
  /* eslint-disable-next-line class-methods-use-this */
  getClickableLinks(placementId) {
    if (this.dataset.adType === 'bing') {
      return document.querySelectorAll(`#${placementId} .link_wrapper a`);
    }
    return document.querySelectorAll(`#${placementId} .link_wrapper`);
  }

  generateClickHandler(placementId, link, linkIdx) {
    return () => {
      const { logger } = this.adPlacements[placementId];
      this.clickHandler(logger, link, linkIdx);
    };
  }

  clickHandler(logger, link, linkIdx) {
    const {
      adType, adOpen, dabuClickUrl, dabuCampaignId, dabuLp, dabuPid,
    } = this.dataset;
    let finalUrl;
    if (adType === 'sib') {
      /* if the campaignId still looks like a macro,
       * we should send clicks to the base landing page
       * as determined by the lp querystring param
       */
      if (!this.isMacroResolved(dabuCampaignId) && this.isMacroResolved(dabuClickUrl) && dabuLp) {
        if (adOpen === 'self') window.location.href = dabuClickUrl + dabuLp;
        window.open(dabuClickUrl + dabuLp);
        return;
      }

      const { lpUrl } = link.dataset;
      const { abTestBingTypeTag } = this.adContext || {};
      // eslint-disable-next-line max-len
      const typetag = abTestBingTypeTag || (this.agenciesMap[dabuPid] || this.agenciesMap.amg).typeTag;
      finalUrl = this.isFailOver
        ? `${dabuClickUrl + lpUrl}&rtb=${typetag}`
        : this.buildSibLpUrl(dabuClickUrl, lpUrl);
      // window.open(finalUrl);
      if (adOpen === 'self') window.location.href = finalUrl;
      else window.open(finalUrl);
    } else {
      // bing links self-open
      this.fireConversionPixel(linkIdx);
    }
    const gaPayload = logger.getClickPayload(
      link.dataset.index,
      link.textContent,
      finalUrl,
      this.isFirstAdBlock(logger.placementId),
    );
    logger.logEvent(gaPayload);
    logger.debug('Handle click event', 'landing page URL', finalUrl);
  }

  /* eslint-disable-next-line class-methods-use-this */
  attachClickHandlers(elements, placementId) {
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      const link = el.querySelector('a');
      const handleClick = this.generateClickHandler(placementId, link || elements[i], i);
      el.addEventListener('click', handleClick, false);
    }
  }

  // Returns is current block(with provided placementId) is the first block on the content page
  isFirstAdBlock = (placementId) => {
    const { dabuPlacementId = '' } = this.dataset;
    const adPlacements = dabuPlacementId.split(',');

    return Array.isArray(adPlacements) && adPlacements.length && adPlacements[0] === placementId;
  };

  // Returns no of Ad block links present in DOM for specific blocks
  // TODO: calculate the num of ads displayed from Dabu for Bing and SIB, counting number of
  // .link_wrapper from front end is unreliable due to the timing of the element being mounted
  getNoOfAdBlockResults = (selectorEle) => {
    let adResultsDisplayedTop;
    let adResultsDisplayedBottom;
    const { dabuPlacementId = '' } = this.dataset;
    const _adPlacements = dabuPlacementId.split(',');
    // clean empty mount Ids.
    const adPlacements = _adPlacements.filter((mountId) => mountId !== '');
    if (Array.isArray(adPlacements) && adPlacements.length) {
      // eslint-disable-next-line max-len
      const topBlocklinks = document.getElementById(adPlacements[0]).querySelectorAll(selectorEle) || [];
      adResultsDisplayedTop = topBlocklinks ? topBlocklinks.length : 0;
      const bottomBlocklinks = adPlacements[1]
        ? document.getElementById(adPlacements[1]).querySelectorAll(selectorEle)
        : [];
      adResultsDisplayedBottom = bottomBlocklinks.length;
    }
    return {
      adResultsDisplayedTop,
      adResultsDisplayedBottom,
      adResultsDisplayed: adResultsDisplayedTop + adResultsDisplayedBottom,
    };
  };

  // FAILOVER METHODS
  handleFailOver() {
    const { dabuPid } = this.dataset;

    this.isFailOver = true;

    const template = this.failSafeTemplate();

    const failSafeResponse = {
      eventType: 'FailOver',
      typeTag: (this.agenciesMap[dabuPid] || this.agenciesMap.amg).typeTag,
      isGenericResponse: true,
      oCode: 1472137,
      templateName: `${template.name}_FailSafe`,
      content: template.content,
      stylesInnerText: template.stylesInnerText,
      results: template.pageResults,
      partner: 'failsafe',
    };

    return failSafeResponse;
  }

  failSafeTemplate() {
    // TODO: generate content and styleInnerText using webpack
    const { dabuWidth, dabuHeight } = this.dataset;
    let template = failSafeTemplate[`${dabuWidth}x${dabuHeight}`];
    if (template === undefined) {
      template = failSafeTemplate['300x250'];
    }
    return template;
  }

  // Polling Fns
  //   note: upon failure, a polling function should enqueue itself again.
  //   doPolling does not know about success or failure of independent functions.
  enqueuePollingfn(fn) {
    this.pollingFns.push(fn);
    if (!this.isCurrentlyPolling) {
      this.isCurrentlyPolling = true;
      this.startPolling();
    }
  }

  startPolling() {
    this.win.setTimeout(this.doPolling.bind(this), 750);
  }

  doPolling() {
    const { pollingFns } = this;
    this.pollingFns = [];

    pollingFns.forEach((fn) => fn.call(this));

    // keep going or stop
    if (this.pollingFns.length) {
      this.win.setTimeout(this.doPolling.bind(this), 750);
    } else {
      this.isCurrentlyPolling = false;
    }
  }

  // UTIL METHODS
  isMacroResolved(param) {
    if (!param) {
      return true;
    }
    const macroRegex = /\${\w*}/g;
    const macroFound = param.match(macroRegex);
    if (macroFound) {
      this.logger.error(`${param} Macro isn't being resolved correctly`);
    }
    return !macroFound;
  }

  appendABTestChannel(primaryCh, abTestCh) {
    return `${primaryCh}${abTestCh ? `,${abTestCh}` : ''}`;
  }

  // STATIC METHODS
  static generateUUID() {
    let dt = new Date().getTime();
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (dt + Math.random() * 16) % 16 | 0;
      dt = Math.floor(dt / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    return uuid.toUpperCase();
  }

  static hasAllMonetizationParams(params) {
    if (!params) {
      return false;
    }
    return (
      params.ag && params.ad && params.an && (params.rch || params.crch || params.rtb || params.rty)
    );
  }

  static containsArchieClickUrl(urls) {
    const clickUrl = typeof urls === 'string' ? [urls] : urls;
    const clkUrlsCount = clickUrl.reduce((acc, url) => acc + (url.indexOf('_clk') > -1), 0);
    return clkUrlsCount > 0;
  }
}

// SIB Ads Methods
AmgAds.prototype.buildSibDabuUrl = buildSibDabuUrl;
AmgAds.prototype.getUnmountedPlacements = getUnmountedPlacements;
AmgAds.prototype.getSibLinks = getSibLinks;
AmgAds.prototype.mountSibsToTargetNodes = mountSibsToTargetNodes;
AmgAds.prototype.mountSibResponse = mountSibResponse;

// Bing Ads Methods
AmgAds.prototype.mountBingResponse = mountBingResponse;
AmgAds.prototype.mountTelemetryScript = mountTelemetryScript;
AmgAds.prototype.generateTelemetryScripts = generateTelemetryScripts;
AmgAds.prototype.getBingAds = getBingAds;

// Bing JS Ads Methods
AmgAds.prototype.mountBingJsScript = mountBingJsScript;
AmgAds.prototype.getBingJSAds = getBingJSAds;

// AFS Ads Methods
AmgAds.prototype.mountGoogleAFSAds = mountGoogleAFSAds;
AmgAds.prototype.getGoogleAFSParams = getGoogleAFSParams;

// GRS Ads Methods
AmgAds.prototype.mountGoogleRS = mountGoogleRS;
AmgAds.prototype.getGoogleRSParams = getGoogleRSParams;

// GRS Ads Methods
AmgAds.prototype.mountGoogleRSV2 = mountGoogleRSV2;
AmgAds.prototype.getGoogleRSParamsV2 = getGoogleRSParamsV2;

// Just Watch Widget Method
AmgAds.prototype.mountJustWatchWidget = mountJustWatchWidget;

// MGM Ads Method
AmgAds.prototype.mountMgmAds = mountMgmAds;
AmgAds.prototype.mountMgmAdByPlacementId = mountMgmAdByPlacementId;

// Pixel Methods
AmgAds.prototype.initPixelHandler = initPixelHandler;
AmgAds.prototype.firePageViewPixel = firePageViewPixel;
AmgAds.prototype.getGoogleAdsClickTrackUrl = getGoogleAdsClickTrackUrl;
AmgAds.prototype.getBingJsAdsClickTrackUrl = getBingJsAdsClickTrackUrl;
AmgAds.prototype.getAfsConversionPixelUrls = getAfsConversionPixelUrls;
AmgAds.prototype.fireConversionPixel = fireConversionPixel;
AmgAds.prototype.watchForCsaCookie = watchForCsaCookie;
AmgAds.prototype.getDocumentCookies = getDocumentCookies;

export default AmgAds;
